import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLoginResult } from "../State/LoginResultContext";
import { API_URL } from "../utils/constants";
import { useErrorRenderer } from "../Hooks/useErrorRenderer";
import { showToast } from "../utils/toast";
import OrganizationTypeSelect from "../Components/OrganizationTypeSelect";

const organizationTypesKeywordsMapping = {
  "church": "glossolalia, tarrying, impartation, deliverance, sanctification, consecration, intercessory, outpouring, apostolic, evangelistic, missionary, convocation, hallelujah, tabernacle, doxology, benediction, redemption, atonement, righteousness, repentance, discipleship, congregation, epistle, tambourine, shofar, prophetic, discernment, Nebuchadnezzar, Melchizedek, Zerubbabel, Belshazzar, Methuselah, Nebuzaradan, Mephibosheth, Ahasuerus, Zacchaeus, Bartimaeus, Nicodemus, Epaphroditus, Onesimus, Theophilus, Tychicus, Habakkuk, Zephaniah, Capernaum, Bethsaida, Chorazin, Cappadocia, Sanhedrin, Caiaphas",
  "k12_school": "headteacher, SENCO, Ofsted, SATs, valedictorian, salutatorian, EHCP, IEP, invigilator, UCAS, pedagogy, safeguarding, matriculation, dyslexia, dyspraxia, dyscalculia, baccalaureate, truancy, demerit, commendation, vocational, apprenticeship, counselling, numeracy, phonics, keystage, dormitory, chaperone, GCSE, A-level, half-term, sixth-form, invigilation, moderation, scaffolding, remediation, mentorship, captaincy, governors, headship, bursar, bursary, exclusion, expulsion, prefect, homeroom, transcript, prospectus, enrolment, SEND",
  "university_college": "matriculation, convocation, provost, registrar, chancellor, vice-chancellor, postgraduate, undergraduate, doctorate, doctoral, dissertation, colloquium, sabbatical, emeritus, adjunct, postdoctoral, peer-review, plenary, mortarboard, hooding, invigilator, invigilation, plagiarism, viva, tenure, syllabus, accreditation, matriculand, graduand, quadrangle, refectory, bursarial, baccalaureate, associateship, professorship, fellowship, symposium, bursary, scholarship, endowment, alumni, fresher, freshers, commencement, regalia, dean, faculty, senate, varsity, cohort",
  "nonprofit": "grantmaking, philanthropy, beneficiary, bequest, stewardship, cultivation, crowdfunding, trustee, bylaws, constituent, fiscal-sponsor, grassroots, 990-form, whistleblower, tax-deductible, tax-exempt, capital-campaign, annual-fund, gift-aid, fiduciary, disbursement, grantee, grantor, subgrant, subrecipient, indirect-costs, cost-share, logframe, theory-of-change, logic-model, baseline, endline, marginalized, capacity-building, technical-assistance, coalition-building, stakeholder, endowment, donor, auction, sponsorship, advocacy, compliance, reporting, metrics, outcomes, underserved, sustainability, replicability, scalability",
  "government": "ordinance, quorum, referendum, alderman, selectboard, municipality, zoning, rezoning, annexation, condemnation, easement, eminent-domain, stormwater, franchise-agreement, home-rule, intergovernmental, interlocal, redevelopment, canvass, gerrymandering, redistricting, apportionment, comptroller, assessor, ombudsman, arbitrator, indemnification, jurisdiction, precinct, constituency, sole-source, adjudication, enforcement-officer, district-board, councilmember, commissioner, auditor, treasurer, recorder, sheriff, marshal, mediator, litigation, liability, nominee, ratification, municipal, clerk, levy, variance",
  "conference": "keynote, plenary, panelist, exhibitor, whitepaper, embargo, greenroom, teleprompter, masterclass, ignite, hackathon, accreditation, chairperson, drayage, teardown, dietary, assistive, welcome-kit, credential, delegate, plenary-hall, breakout-room, dais, emcee, toastmaster, laureate, laureateship, colloquium, symposium, convening, plenary-session, town-hall, fireside, pitch-competition, demo-day, incubator, accelerator, sponsorship-tier, platinum-sponsor, exhibitor-hall, expo, floorplan, moderator, facilitator, showcase, rehearsal, interpretation, proceedings, abstract, registration"
};

export default function Profile() {
  const [message, setMessage, isError, setIsError, resetErrorState] =
    useErrorRenderer();

  const navigate = useNavigate();
  const { loginResults, updateloginResults } = useLoginResult();
  const [fullName, setFullName] = useState(loginResults?.fullName ?? "");
  const [organization, setOrganization] = useState(loginResults.activeOrganization.name ?? "");
  const [organizationType, setOrganizationType] = useState(
    loginResults?.activeOrganization?.organizationType ?? ""
  );
  const [primaryVenue, setPrimaryVenue] = useState(loginResults?.primaryVenue ?? "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        id: loginResults.id,
        fullName: fullName,
        organization: organization,
        organizationType: organizationType,
        organizationId: loginResults.activeOrganization.id,
        primaryVenue: primaryVenue,
      };

      const response = await fetch(`${API_URL}/users/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${loginResults.token}`,
        },
        body: JSON.stringify(body),
      });

      let keywords = organizationTypesKeywordsMapping[organizationType] || "";

      const data = await response.json();
      if (response.status === 200) {
        const updated = {
          ...loginResults,
          fullName: fullName,
          organization: organization,
          primaryVenue: primaryVenue,
          keywords,
          activeOrganization: {
            ...loginResults.activeOrganization,
            organizationType: organizationType,
          },
        };
        showToast("Profile updated successfully.", { type: "success" });
        updateloginResults(updated);
        navigate("/my-events");
      } else {
        setMessage(data.message);
        setIsError(true);
        resetErrorState();
      }
    } catch (error) {
      setMessage(error.message);
      setIsError(true);
      resetErrorState();
    }
  };

  return (
    <div className="min-h-dvh flex flex-col">
      <div className="flex flex-1 flex-col items-center px-6 py-10 sm:py-14">
        <div className="w-full sm:mx-auto sm:w-full sm:max-w-xl">
          <h2 className="text-center text-[32px] sm:text-4xl sm:text-[40px] font-bold tracking-tight text-white atkinson-font">
            Edit Profile
          </h2>
        </div>

        <div className="mt-10 w-full sm:mx-auto sm:w-full sm:max-w-xl">
          <form onSubmit={handleSubmit} method="POST" className="space-y-6 mx-2 sm:mx-4">
            <div>
              <label
                htmlFor="fullName"
                className="block text-2xl font-bold text-white text-left atkinson-font"
              >
                Full Name
              </label>
              <div className="mt-2">
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  onChange={(e) => setFullName(e.target.value)}
                  value={fullName}
                  required
                  placeholder="Update your name"
                  className="block w-full rounded-md border border-[#FFFFFFCC] py-3 px-3 bg-[#595959] text-[#FFFFFF] text-base placeholder:text-[#FFFFFFCC] focus:border-[#FFFFFF]"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="organization"
                className="block text-2xl font-bold text-white text-left atkinson-font"
              >
                Organization Name
              </label>
              <div className="mt-2">
                <input
                  id="organization"
                  name="organization"
                  type="text"
                  required
                  onChange={(e) => setOrganization(e.target.value)}
                  value={organization}
                  placeholder="Update your organization’s name"
                  className="block w-full rounded-md border border-[#FFFFFFCC] py-3 px-3 bg-[#595959] text-[#FFFFFF] text-base placeholder:text-[#FFFFFFCC] focus:border-[#FFFFFF]"
                />
              </div>
            </div>

            <div>
              <OrganizationTypeSelect
                value={organizationType}
                onChange={setOrganizationType}
                placeholder="Update your organization type"
                buttonClassName="!py-3 text-base"
              />
            </div>

            <div>
              <label
                htmlFor="primaryVenue"
                className="block text-2xl font-bold text-white text-left atkinson-font"
              >
                Primary Venue
              </label>
              <div className="mt-2">
                <input
                  id="primaryVenue"
                  name="primaryVenue"
                  type="text"
                  onChange={(e) => setPrimaryVenue(e.target.value)}
                  value={primaryVenue}
                  placeholder="Update your primary venue"
                  className="block w-full rounded-md border border-[#FFFFFFCC] py-3 px-3 bg-[#595959] text-[#FFFFFF] text-base placeholder:text-[#FFFFFFCC] focus:border-[#FFFFFF]"
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                className="flex w-full justify-center rounded-md !bg-[#5701AE] py-3 px-3 text-base font-semibold text-white hover:!bg-[#7A2FD3]"
                style={{ border: "2px solid #6A6680" }}
              >
                Update Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
